import { useState, useEffect, CSSProperties } from 'react'
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import useScreen from 'Hooks/useScreen'
import useBrand from 'Hooks/useBrand'
import Colors from 'modules/Colors'
import ButtonSymphony from 'elements/ButtonSymphony'
import ModalSymphony from 'elements/ModalSymphony'
import Icon from 'elements/Icon'
import { clsx, formatNumberToDisplayType, showToast, styx } from 'modules/Utils'
import {
    TaskPackOption,
    getTaskPackOptions_api,
    createTaskPackPayment_api,
    completeTaskPackPurchase_api
} from 'api/agentApi'
import Banner from 'elements/Banner'
import useMaestroChat from 'Hooks/useMaestroChat'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')

interface TaskPackPurchaseModalProps {
    isOpen: boolean
    onClose: () => void
    onSuccess?: (tasksAdded: number) => void
}

const TaskPackPurchaseModal = ({ isOpen, onClose, onSuccess }: TaskPackPurchaseModalProps) => {
    const { mobileView, mStyle } = useScreen()
    const { currentBrand } = useBrand()

    const {
        isChatOpen,
        sendMessage
    } = useMaestroChat()

    const [step, setStep] = useState<'selection' | 'payment'>('selection')
    const [taskPackOptions, setTaskPackOptions] = useState<TaskPackOption[]>([])
    const [selectedPack, setSelectedPack] = useState<TaskPackOption | null>(null)
    const [clientSecret, setClientSecret] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [paymentFormRef, setPaymentFormRef] = useState<{ submitPayment: () => void } | null>(null)

    // Diagnostic check for Stripe configuration
    useEffect(() => {
        const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
        console.log('Stripe diagnostic:', {
            keyConfigured: !!stripeKey,
            keyPrefix: stripeKey ? stripeKey.substring(0, 7) + '...' : 'Missing'
        })

        if (!stripeKey) {
            console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not configured')
            setError('Payment system configuration error. Please contact support.')
        }
    }, [])

    // Load task pack options on mount
    useEffect(() => {
        if (isOpen) {
            loadTaskPackOptions()
        }
    }, [isOpen])

    const loadTaskPackOptions = async () => {
        try {
            setIsLoading(true)
            const options = await getTaskPackOptions_api()
            setTaskPackOptions(options)
        } catch (error) {
            console.error('Failed to load task pack options:', error)
            setError('Failed to load task pack options. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handlePackSelect = async (pack: TaskPackOption) => {
        if (!currentBrand?.id) return

        try {
            setIsLoading(true)
            setError(null)
            console.log('Creating payment for pack:', pack)

            const { clientSecret } = await createTaskPackPayment_api({
                brandId: currentBrand.id,
                packSize: pack.size
            })

            console.log('Payment created, clientSecret:', clientSecret ? 'Received' : 'Missing')
            setSelectedPack(pack)
            setClientSecret(clientSecret)
            setStep('payment')
        } catch (error) {
            console.error('Failed to create payment:', error)
            setError('Failed to create payment. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handlePaymentSuccess = async (paymentIntentId: string) => {
        if (!currentBrand?.id || !selectedPack) return

        try {
            setIsLoading(true)

            const result = await completeTaskPackPurchase_api({
                brandId: currentBrand.id,
                paymentIntentId,
                packSize: selectedPack.size
            })

            console.log('result')
            if (result.success) {
                // Refresh action usage to update task counter
                // Call success callback
                onSuccess?.(result.tasksAdded)

                // Close modal
                handleClose()

                console.log('isChatOpen', isChatOpen)

                if (isChatOpen) {
                    sendMessage(`Just unlocked ${selectedPack.size} more tasks!`)
                } else {
                    showToast({
                        message: `You've unlocked ${selectedPack.size} Maestro tasks!`,
                        error: false,
                        mobile: mobileView
                    })
                }
            } else {
                setError(result.message || 'Purchase failed. Please try again.')
            }
        } catch (error) {
            console.error('Failed to complete purchase:', error)
            setError('Failed to complete purchase. Please contact support.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        setStep('selection')
        setSelectedPack(null)
        setClientSecret('')
        setError(null)
        onClose()
    }

    const handleBackToSelection = () => {
        setStep('selection')
        setSelectedPack(null)
        setClientSecret('')
        setError(null)
    }

    const handleCheckoutClick = () => {
        if (paymentFormRef) {
            paymentFormRef.submitPayment()
        }
    }

    const renderTaskPackOption = (pack: TaskPackOption) => {
        return (
            <div
                key={pack.size}
                className="taskPackOption"
                style={mStyle(sty, 'taskPackOption')}
            >
                <div className="taskPackInner" style={sty.taskPackInner}>
                    <div className="taskPackCardTitle" style={sty.taskPackCardTitle}>
                        <Icon
                            className="taskPackIcon"
                            style={sty.taskPackIcon}
                            icon="robot"
                            color={Colors.purple}
                            size={32}
                            isCircle
                        />
                        <span>{pack.size} Tasks</span>
                    </div>

                    <div className="taskPackPriceContainer" style={sty.taskPackPriceContainer}>
                        <div className="taskPackPrice" style={sty.taskPackPrice}>
                            {formatNumberToDisplayType(pack.totalPriceCents / 100, 'currency')}
                        </div>
                    </div>
                    <ButtonSymphony
                        className="selectPackButton"
                        style={sty.selectPackButton}
                        disabled={isLoading}
                        onClick={() => handlePackSelect(pack)}
                    >
                        Select Pack
                    </ButtonSymphony>
                </div>
            </div>
        )
    }

    const renderFeatures = () => {
        return (
            <Banner
                color="purple"
                className="taskPackFeaturesSection"
            >
                <div className="featureItem" style={sty.featureItem}>
                    <Icon icon="check" size={16} style={sty.featureIcon} className="featureIcon" />
                    <span>Let Maestro do the work for you.</span>
                </div>
                <div className="featureItem" style={sty.featureItem}>
                    <Icon icon="check" size={16} style={sty.featureIcon} className="featureIcon" />
                    <span>Secure payment with Stripe</span>
                </div>
                <div className="featureItem" style={sty.featureItem}>
                    <Icon icon="check" size={16} style={sty.featureIcon} className="featureIcon" />
                    <span>Purchased tasks expire after 1 year</span>
                </div>
            </Banner>
        )
    }

    const renderSelectionStep = () => {
        return (
            <div className="taskPackSelection" style={sty.taskPackSelection}>
                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        <Icon icon="warning" size={16} className="errorMessageIcon" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="taskPackOptions" style={mStyle(sty, 'taskPackOptions')}>
                    {isLoading ? (
                        <div className="loadingState" style={sty.loadingState}>
                            <Icon icon="maestroBadgeImage" size={48} style={sty.loadingIcon} className="loadingIcon" />
                            <span style={sty.loadingText}>Loading options...</span>
                        </div>
                    ) : (
                        taskPackOptions.map(pack => renderTaskPackOption(pack))
                    )}
                </div>

                {!isLoading && renderFeatures()}
            </div>
        )
    }

    const renderPaymentStep = () => {
        console.log('renderPaymentStep called - selectedPack:', !!selectedPack, 'clientSecret:', !!clientSecret)

        if (!selectedPack || !clientSecret) {
            console.log('Missing data for payment step:', { selectedPack: !!selectedPack, clientSecret: !!clientSecret })
            return null
        }

        return (
            <div className="taskPackPayment" style={sty.taskPackPayment}>
                <div className="paymentSummary" style={sty.paymentSummary}>
                    <div className="summaryDetails" style={sty.summaryDetails}>
                        <span>{selectedPack.size} Tasks</span>
                        <span style={sty.summaryPrice}>
                            {formatNumberToDisplayType(selectedPack.totalPriceCents / 100, 'currency')}
                        </span>
                    </div>
                </div>

                {error && (
                    <div className="errorMessage" style={sty.errorMessage}>
                        <Icon icon="warning" size={16} className="errorMessageIcon" />
                        <span>{error}</span>
                    </div>
                )}

                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <PaymentForm
                        onSuccess={handlePaymentSuccess}
                        amount={selectedPack.totalPriceCents}
                        isLoading={isLoading}
                        setError={setError}
                        onFormReady={setPaymentFormRef}
                    />
                </Elements>
            </div>
        )
    }

    const getModalProps = () => {
        if (step === 'payment') {
            return {
                buttonLeft: 'Back',
                buttonRight: 'Checkout',
                onButtonLeftClick: handleBackToSelection,
                onButtonRightClick: handleCheckoutClick,
                buttonRightLoading: isLoading
            }
        }
        return {}
    }

    return (
        <ModalSymphony
            className="taskPackPurchaseModal"
            isOpen={isOpen}
            onClose={handleClose}
            fullScreen={mobileView}
            title={step === 'selection' ? "Purchase More Tasks" : "Complete Purchase"}
            subtitle={step === 'selection' ? "Get more tasks to unlock Maestro's full potential. Each task lets Maestro take one action on your behalf." : ""}
            style={sty.modal}
            {...getModalProps()}
        >
            <div className="taskPackModalContent" style={mStyle(sty, 'modalContent')}>
                {step === 'selection' ? renderSelectionStep() : renderPaymentStep()}
            </div>
        </ModalSymphony>
    )
}

// Payment form component that uses Stripe Elements
const PaymentForm = ({
    onSuccess,
    amount,
    isLoading,
    setError,
    onFormReady
}: {
    onSuccess: (paymentIntentId: string) => void
    amount: number
    isLoading: boolean
    setError: (error: string | null) => void
    onFormReady: (formRef: { submitPayment: () => void }) => void
}) => {
    const stripe = useStripe()
    const elements = useElements()
    const { currentBrand } = useBrand()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isElementsLoading, setIsElementsLoading] = useState(true)

    // Debug logging
    useEffect(() => {
        console.log('PaymentForm mounted - stripe:', !!stripe, 'elements:', !!elements)
        console.log('Stripe public key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 'Set' : 'Missing')
    }, [])

    useEffect(() => {
        if (stripe && elements) {
            console.log('Stripe and elements ready, calling onFormReady')
            onFormReady({ submitPayment: handleSubmit })
        }
    }, [stripe, elements, onFormReady])

    const handleSubmit = async (event?: React.FormEvent) => {
        if (event) {
            event.preventDefault()
        }
        setError(null)

        if (!stripe || !elements) {
            console.log('Stripe or elements not ready:', { stripe: !!stripe, elements: !!elements })
            return
        }

        try {
            setIsSubmitting(true)

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            })

            if (error) {
                console.error('Stripe payment error:', error)
                setError(error.message || 'Payment failed. Please try again.')
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                console.log('Payment succeeded:', paymentIntent.id)
                await onSuccess(paymentIntent.id)
            }
        } catch (error) {
            console.error('Payment confirmation failed:', error)
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Show loading state while Stripe is initializing
    if (!stripe || !elements) {
        return (
            <div className="paymentFormLoading" style={sty.paymentFormLoading}>
                <Icon icon="maestroBadgeImage" size={32} style={sty.loadingIcon} className="loadingIcon" />
                <span>Loading payment form...</span>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} style={sty.paymentForm}>
            <div className="paymentElementContainer" style={sty.paymentElementContainer}>
                <PaymentElement
                    options={{
                        layout: 'tabs',
                        defaultValues: {
                            billingDetails: {
                                name: currentBrand?.name || '',
                            }
                        },
                        // Show saved payment methods prominently for returning customers
                        fields: {
                            billingDetails: 'auto'
                        }
                    }}
                    onReady={() => {
                        console.log('PaymentElement ready')
                        setIsElementsLoading(false)
                    }}
                    onLoaderStart={() => {
                        console.log('PaymentElement loader started')
                        setIsElementsLoading(true)
                    }}
                    onChange={(event) => {
                        console.log('PaymentElement change:', event)
                        // Clear any existing errors when user interacts with form
                        if (event.complete) {
                            setError(null)
                        }
                    }}
                />

                {isElementsLoading && (
                    <div className="paymentElementLoading" style={sty.paymentElementLoading}>
                        <span>Loading payment options...</span>
                    </div>
                )}

            </div>

            <ButtonSymphony
                type="submit"
                className="submitPaymentButton"
                style={sty.submitPaymentButton}
                onClick={handleSubmit}
                size="large"
                isLoading={isSubmitting}
                disabled={isSubmitting || !stripe || isLoading}
            >
                {isSubmitting ? 'Processing Payment...' : `Add Card and Pay`}
            </ButtonSymphony>


        </form>
    )
}

const sty: Record<string, CSSProperties> = {
    modal: {
        maxWidth: 750,
    },
    modalContent: {
        padding: 18,
    },
    modalContent_mobile: {
        padding: 4,
    },
    taskPackSelection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
    },
    submitPaymentButton: {
        width: '100%',
    },
    taskPackOptions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        justifyContent: 'center',
    },
    taskPackOptions_mobile: {
        flexDirection: 'column',
    },
    taskPackOption: {
        position: 'relative',
        width: '100%',
        maxWidth: 280,
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 16,
        backgroundColor: 'white',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    taskPackOption_mobile: {
        maxWidth: '100%',
        textAlign: 'center',
    },
    taskPackInner: {
        padding: 16,
    },
    taskPackCardTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        fontSize: 24,
        fontWeight: 500,
        marginBottom: 16,
    },
    taskPackIcon: {
        flexShrink: 0,
        backgroundColor: Colors.purpleLightest,
    },
    taskPackPriceContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 12,
    },
    taskPackPrice: {
        fontSize: 40,
        fontWeight: 500,
        color: Colors.purple,
    },
    taskPackSavings: {
        fontSize: 12,
        color: Colors.green,
        fontWeight: 500,
        marginBottom: 16,
        textAlign: 'center',
        padding: '4px 8px',
        backgroundColor: '#f0f9f4',
        borderRadius: 4,
    },
    selectPackButton: {
        width: '100%',
        padding: 16,
        fontSize: 16,
        fontWeight: 500,
    },
    taskPackFeaturesSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        padding: '24px 0',
        borderTop: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: Colors.textColor,
    },
    featureIcon: {
        color: Colors.green,
        flexShrink: 0,
    },
    taskPackPayment: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
    paymentSummary: {
        textAlign: 'center',
    },
    paymentTitle: {
        fontSize: 24,
        fontWeight: 500,
        color: Colors.textColor,
        margin: 0,
        marginBottom: 8,
    },
    summaryDetails: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 16,
        color: Colors.greyDark,
        padding: '12px 16px',
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
    },
    summaryPrice: {
        fontWeight: 500,
        color: Colors.purple,
    },
    paymentForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    paymentElementContainer: {
        padding: 16,
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 8,
        backgroundColor: 'white',
    },
    paymentSecurity: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontSize: 12,
        color: Colors.greyDark,
    },
    errorMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        backgroundColor: '#fef2f2',
        borderRadius: 8,
        border: `1px solid ${Colors.red}`,
        color: Colors.red,
        fontSize: 14,
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 60,
        gap: 16,
        color: Colors.greyDark,
        gridColumn: '1 / -1',
    },
    loadingIcon: {
        opacity: 0.7,
    },
    loadingText: {
        fontSize: 16,
        fontWeight: 500,
    },
    paymentFormLoading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 12,
        color: Colors.greyDark,
        fontSize: 14,
    },
    paymentElementLoading: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: 12,
        color: Colors.greyDark,
        pointerEvents: 'none',
    },
}

export default TaskPackPurchaseModal 